import type { ReactNode } from 'react';
import { formatCurrency } from '../../utils/formatCurrency';

export type BalanceTotalsBarMode = 'selected' | 'page';

export interface BalanceTotalsBarProps {
  mode: BalanceTotalsBarMode;
  count: number;
  primaryLabel: string;
  primaryAmount: number;
  secondaryLabel?: string;
  secondaryAmount?: number;
  pageCountSuffix?: string;
  onClear?: () => void;
  children?: ReactNode;
}

const BalanceTotalsBar = ({
  mode,
  count,
  primaryLabel,
  primaryAmount,
  secondaryLabel,
  secondaryAmount,
  pageCountSuffix = '',
  onClear,
  children,
}: BalanceTotalsBarProps) => {
  const countLabel =
    mode === 'selected'
      ? `${count} selected`
      : `Totals (${count}${pageCountSuffix})`;

  const showSecondary =
    secondaryLabel != null && secondaryAmount != null;

  return (
    <div className="z-20 flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span className="font-medium text-gray-900">{countLabel}</span>
        <span className="text-gray-600">
          {primaryLabel}{' '}
          <span
            className={`font-medium ${
              primaryAmount > 0 ? 'text-red-600' : 'text-gray-900'
            }`}
          >
            {formatCurrency(primaryAmount)}
          </span>
        </span>
        {showSecondary && (
          <span className="text-gray-600">
            {secondaryLabel}{' '}
            <span
              className={`font-medium ${
                secondaryAmount > 0 ? 'text-amber-700' : 'text-gray-900'
              }`}
            >
              {formatCurrency(secondaryAmount)}
            </span>
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        {children}
        {mode === 'selected' && onClear && (
          <button
            type="button"
            onClick={onClear}
            className="text-sm text-slate-700 underline hover:text-slate-900"
          >
            Clear selection
          </button>
        )}
      </div>
    </div>
  );
};

export default BalanceTotalsBar;
